// VideoEmbedBlockEditor — URL-based video/audio embed.
//
// Просто paste URL — auto-detect provider + preview iframe. Поддерживаются:
//   Video: YouTube, Vimeo, Instagram, TikTok
//   Audio: SoundCloud, Spotify, Apple Music, Bandcamp
//   Aspect ratio автоматически предлагается (9:16 для TikTok/Instagram).
//
// Для нераспознанных URL (Twitter, Pinterest, Artlist, others) — fallback
// "Preview not available, will render as link on the article page".

import { useEffect, useMemo } from "react";
import type { VideoEmbedBlock, Block, MediaWidth } from "../../../lib/blocks";
import { detectProvider, embedUrlFor, defaultAspectFor } from "../../../lib/blocks";
import { t } from "../theme";

export interface VideoEmbedBlockEditorProps {
  block: VideoEmbedBlock;
  onChange: (patch: Partial<Block>) => void;
  isSelected: boolean;
}

const WIDTHS: MediaWidth[] = ["narrow", "regular", "wide", "full"];
const RATIOS: VideoEmbedBlock["aspectRatio"][] = ["16:9", "9:16", "4:3", "1:1"];

// Human-readable label for provider chip.
const PROVIDER_LABELS: Record<string, string> = {
  youtube: "YouTube",
  vimeo: "Vimeo",
  instagram: "Instagram",
  tiktok: "TikTok",
  soundcloud: "SoundCloud",
  spotify: "Spotify",
  apple_music: "Apple Music",
  bandcamp: "Bandcamp",
  pinterest: "Pinterest",
  artlist: "Artlist",
  twitter: "X / Twitter",
  other: "Other",
};

export function VideoEmbedBlockEditor({
  block,
  onChange,
  isSelected,
}: VideoEmbedBlockEditorProps) {
  function setUrl(url: string) {
    const provider = detectProvider(url);
    onChange({ url, provider } as Partial<VideoEmbedBlock>);
  }

  const provider = block.provider ?? detectProvider(block.url);
  const embedSrc = useMemo(
    () => (block.url ? embedUrlFor(provider, block.url) : null),
    [block.url, provider],
  );

  // Auto-suggest aspect ratio when provider changes (TikTok / Instagram → 9:16).
  // Применяем только если юзер не задал aspect руками — для этого считаем
  // его дефолтным если поле не отличается от standard 16:9.
  useEffect(() => {
    if (!block.url) return;
    const suggested = defaultAspectFor(provider);
    if (!block.aspectRatio && suggested !== "16:9") {
      onChange({ aspectRatio: suggested } as Partial<VideoEmbedBlock>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  const aspectPad =
    block.aspectRatio === "9:16" ? "177.78%" :
    block.aspectRatio === "4:3" ? "75%" :
    block.aspectRatio === "1:1" ? "100%" :
    "56.25%"; // 16:9

  return (
    <div>
      {isSelected && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8, fontFamily: t.sans, flexWrap: "wrap" }}>
          {WIDTHS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onChange({ width: w } as Partial<VideoEmbedBlock>)}
              style={pillStyle((block.width || "wide") === w)}
            >
              {w}
            </button>
          ))}
          <div style={{ width: 8 }} />
          {RATIOS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => onChange({ aspectRatio: r } as Partial<VideoEmbedBlock>)}
              style={pillStyle((block.aspectRatio || "16:9") === r)}
            >
              {r}
            </button>
          ))}
        </div>
      )}

      <input
        type="url"
        value={block.url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="Paste a YouTube, Vimeo, Instagram, TikTok, SoundCloud, Spotify URL…"
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "8px 10px",
          background: t.bg,
          border: `1px solid ${t.border}`,
          borderRadius: 6,
          fontSize: 13,
          color: t.text,
          outline: "none",
          fontFamily: t.sans,
        }}
      />

      {block.url && (
        <div
          style={{
            marginTop: 8,
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            color: t.text3,
            fontFamily: t.sans,
          }}
        >
          <span
            style={{
              padding: "2px 8px",
              borderRadius: 3,
              background: t.accentSoft,
              color: t.accent,
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: "0.02em",
            }}
          >
            {PROVIDER_LABELS[provider] || provider}
          </span>
          {embedSrc && <span>· preview live below</span>}
          {!embedSrc && <span>· renders as link card on article page</span>}
        </div>
      )}

      {embedSrc ? (
        // Аудио провайдеры всегда фиксированной высоты — Spotify/Apple/SoundCloud
        // имеют свой layout, aspect ratio для них не имеет смысла.
        provider === "soundcloud" || provider === "spotify" || provider === "apple_music" || provider === "bandcamp" ? (
          <iframe
            src={embedSrc}
            allow="autoplay; encrypted-media; clipboard-write"
            style={{
              marginTop: 10,
              width: "100%",
              height: provider === "spotify" ? 152 : provider === "apple_music" ? 175 : 166,
              border: `1px solid ${t.border}`,
              borderRadius: 6,
              background: t.bg,
            }}
          />
        ) : (
          <div
            style={{
              position: "relative",
              paddingBottom: aspectPad,
              height: 0,
              marginTop: 10,
              background: "#000",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <iframe
              src={embedSrc}
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: 0,
              }}
            />
          </div>
        )
      ) : (
        block.url && (
          <div
            style={{
              marginTop: 10,
              padding: 14,
              border: `1px solid ${t.border}`,
              borderRadius: 6,
              color: t.text3,
              fontFamily: t.sans,
              fontSize: 12,
              background: t.bg,
            }}
          >
            Preview isn't supported for {PROVIDER_LABELS[provider] || "this provider"} —
            the URL will render as a link card on the article page.
          </div>
        )
      )}

      {isSelected && (
        <input
          type="text"
          value={block.caption || ""}
          onChange={(e) =>
            onChange({ caption: e.target.value } as Partial<VideoEmbedBlock>)
          }
          placeholder="Caption (optional)"
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginTop: 8,
            padding: "6px 8px",
            background: t.bg,
            border: `1px solid ${t.border}`,
            borderRadius: 4,
            fontSize: 12,
            color: t.text,
            outline: "none",
            fontFamily: t.sans,
          }}
        />
      )}
    </div>
  );
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: "3px 8px",
    fontSize: 11,
    borderRadius: 4,
    background: active ? t.accentSoft : "transparent",
    color: active ? t.accent : t.text3,
    border: `1px solid ${active ? t.accentStrong : t.border}`,
    cursor: "pointer",
  };
}
